import type { SupabaseClient } from '@supabase/supabase-js';
import type { PostgrestError } from '@supabase/supabase-js';
import type { EdprRequestRow, BdoScrapedRequest } from '../../types/edpr';
import { EdprRequestDao } from './EdprRequestDao';
import { CompanyValidator } from '../../utils/CompanyValidator';
import { CompanyService } from '../company/CompanyService';
import type { InvalidateFn } from '../base';

// ── Diff types ─────────────────────────────────────────────────────────────

export type DiffAction = 'insert' | 'update' | 'unchanged';

export interface EdprRequestDiff {
  action: DiffAction;
  /** The scraped data converted to DB row shape */
  scraped: Omit<Partial<EdprRequestRow>, 'id' | 'created_at' | 'updated_at'> & { request_number: string };
  /** Existing DB row (null for inserts) */
  existing: EdprRequestRow | null;
  /** Fields that changed (empty for inserts and unchanged) */
  changedFields: string[];
}

// ── Date parsing ───────────────────────────────────────────────────────────

/** Parse BDO date "30-03-2026 11:32" → ISO string */
function parseBdoDate(raw: string | null): string | null {
  if (!raw) return null;
  const m = raw.match(/(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})/);
  if (!m) return raw; // fallback — store as-is
  return `${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}:00`;
}

// ── Fields compared for update detection ───────────────────────────────────

const COMPARE_FIELDS: (keyof EdprRequestRow)[] = [
  'status', 'year', 'request_date', 'submitter',
  'applicant_company', 'applicant_company_bdo', 'applicant_company_nip', 'applicant_phone', 'applicant_email',
  'recipient_company', 'recipient_company_bdo', 'recipient_company_nip',
  'intended_company', 'intended_company_bdo', 'intended_company_nip',
  'waste_code', 'waste_code_description', 'household_waste', 'art24', 'requested_mass', 'additional_info', 'bdo_guid',
];

// ── Service ────────────────────────────────────────────────────────────────

export class EdprRequestService {
  private readonly dao: EdprRequestDao;

  constructor(
    private readonly sb: SupabaseClient,
    invalidateFn?: InvalidateFn,
  ) {
    this.dao = new EdprRequestDao(sb, invalidateFn);
  }

  /** Convert a scraped BDO request to a DB row shape. */
  scrapedToRow(
    scraped: BdoScrapedRequest & { request_number: string },
    companyMap: Map<string, number>,
    userId?: string,
  ): Omit<Partial<EdprRequestRow>, 'id' | 'created_at' | 'updated_at'> & { request_number: string } {
    const applicantNip = CompanyValidator.normalizeNip(scraped.applicant_company_nip);
    const intendedNip = CompanyValidator.normalizeNip(scraped.intended_company_nip);
    // Split "15 01 07 Opakowania ze szkła" → code + description
    const wasteMatch = scraped.waste_code?.match(/^(\d{2}\s*\d{2}\s*\d{2}\*?)\s+(.+)$/);
    const wasteCode = wasteMatch ? wasteMatch[1].trim() : scraped.waste_code;
    const wasteCodeDesc = wasteMatch ? wasteMatch[2].trim() : null;
    // Parse BDO date "30-03-2026 11:32" → ISO
    const requestDate = parseBdoDate(scraped.request_date);
    return {
      request_number: scraped.request_number,
      bdo_guid: scraped.bdo_guid,
      // BDO always supplies a year for a scraped request; the column is NOT NULL.
      // Type-level assertion only — the value is passed through unchanged.
      year: scraped.year as number,
      status: scraped.status,
      request_date: requestDate,
      submitter: scraped.submitter,
      // Applicant
      applicant_company: scraped.applicant_company_name ?? scraped.requesting_company,
      applicant_company_bdo: scraped.applicant_company_bdo,
      applicant_company_nip: applicantNip,
      applicant_company_id: (applicantNip ? companyMap.get(applicantNip) : undefined) ?? null,
      applicant_phone: scraped.applicant_phone,
      applicant_email: scraped.applicant_email,
      // Recipient (us)
      recipient_company: scraped.recipient_company_name,
      recipient_company_bdo: scraped.recipient_company_bdo,
      recipient_company_nip: CompanyValidator.normalizeNip(scraped.recipient_company_nip),
      // Intended (org odzysku)
      intended_company: scraped.intended_company_name ?? scraped.target_company,
      intended_company_bdo: scraped.intended_company_bdo,
      intended_company_nip: intendedNip,
      intended_company_id: (intendedNip ? companyMap.get(intendedNip) : undefined) ?? null,
      // Detail fields
      waste_code: wasteCode,
      waste_code_description: wasteCodeDesc,
      household_waste: scraped.household_waste,
      art24: scraped.art24,
      requested_mass: scraped.requested_mass,
      additional_info: scraped.additional_info,
      synced_at: new Date().toISOString(),
      synced_by: userId ?? null,
    };
  }

  /** Build a NIP→company_id map from the companies table. */
  async buildCompanyNipMap(): Promise<Map<string, number>> {
    const { data, error }: { data: { id: number; nip: string | null }[] | null; error: PostgrestError | null } = await this.sb
      .from('companies')
      .select('id, nip')
      .not('nip', 'is', null);
    if (error) throw error;
    const map = new Map<string, number>();
    for (const c of data ?? []) {
      const normalized = CompanyValidator.normalizeNip(c.nip);
      if (normalized) map.set(normalized, c.id);
    }
    return map;
  }

  /**
   * Compute diff between scraped requests and existing DB rows.
   * Returns an array of diffs ready for user review.
   */
  async computeDiff(
    scraped: BdoScrapedRequest[],
    userId?: string,
  ): Promise<EdprRequestDiff[]> {
    // Filter out rows without a request number (can't upsert without request_number)
    const valid = scraped.filter(
      (s): s is BdoScrapedRequest & { request_number: string } => !!s.request_number,
    );

    const [existingMap, companyMap] = await Promise.all([
      this.dao.getAllByExternalId(),
      this.buildCompanyNipMap(),
    ]);

    const diffs: EdprRequestDiff[] = [];

    for (const s of valid) {
      const row = this.scrapedToRow(s, companyMap, userId);
      const existing = existingMap.get(row.request_number) ?? null;

      if (!existing) {
        diffs.push({ action: 'insert', scraped: row, existing: null, changedFields: [] });
        continue;
      }

      // Compare fields
      const changedFields: string[] = [];
      for (const field of COMPARE_FIELDS) {
        const oldVal = existing[field];
        const newVal = row[field as keyof typeof row];
        if (oldVal !== newVal && !(oldVal == null && newVal == null)) {
          changedFields.push(field);
        }
      }
      // Also check company_id resolutions
      if (existing.applicant_company_id !== row.applicant_company_id && row.applicant_company_id != null) {
        changedFields.push('applicant_company_id');
      }
      if (existing.intended_company_id !== row.intended_company_id && row.intended_company_id != null) {
        changedFields.push('intended_company_id');
      }

      if (changedFields.length === 0) {
        diffs.push({ action: 'unchanged', scraped: row, existing, changedFields: [] });
      } else {
        diffs.push({ action: 'update', scraped: row, existing, changedFields });
      }
    }

    return diffs;
  }

  /** Apply selected diffs (inserts and updates). */
  async applyDiffs(diffs: EdprRequestDiff[]): Promise<EdprRequestRow[]> {
    const toUpsert = diffs
      .filter(d => d.action === 'insert' || d.action === 'update')
      .map(d => d.scraped);
    return this.dao.upsertMany(toUpsert);
  }

  /**
   * Find transactions eligible for an EDPR request.
   * Matches by applicant company family + waste code, ordered by load_date ASC.
   */
  async findMatchingTransactions(
    request: EdprRequestRow,
    txSelect: string,
  ): Promise<unknown[]> {
    if (!request.applicant_company_id) return [];

    const companySvc = new CompanyService(this.sb);
    const { expandedIds } = await companySvc.expandVendorFamily([request.applicant_company_id]);
    if (expandedIds.length === 0) return [];

    let query = this.sb
      .from('transactions_edpr_view')
      .select(txSelect)
      .eq('is_deleted', false)
      .eq('is_edpr_eligible', true)
      .in('load_place_company_id', expandedIds)
      .gt('available_edpr_mass', 0)
      .or('status_override.is.null,status_override.neq.cancelled')
      .order('load_date', { ascending: true });

    if (request.waste_code) {
      query = query.eq('waste_code', request.waste_code);
    }
    if (request.request_date) {
      query = query.lte('load_date', request.request_date.split('T')[0]);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  }

  /**
   * FIFO mass distribution: allocate available_edpr_mass from transactions
   * chronologically until requestedMass is covered.
   * The last transaction gets capped at the remaining need.
   */
  static distributeMass(
    transactions: Array<{ id: number; available_edpr_mass?: number | null }>,
    requestedMass: number,
  ): MassDistribution {
    const allocations = new Map<number, number>();
    let remaining = requestedMass;

    for (const tx of transactions) {
      if (remaining <= 0) break;
      const available = tx.available_edpr_mass ?? 0;
      if (available <= 0) continue;

      const allocated = Math.round(Math.min(available, remaining) * 1000) / 1000;
      allocations.set(tx.id, allocated);
      remaining = Math.round((remaining - allocated) * 1000) / 1000;
    }

    const totalAllocated = Math.round((requestedMass - Math.max(0, remaining)) * 1000) / 1000;
    return {
      allocations,
      totalAllocated,
      deficit: Math.max(0, remaining),
    };
  }
}

// ── Mass distribution ──────────────────────────────────────────────────────

export interface MassDistribution {
  /** transaction_id → allocated mass */
  allocations: Map<number, number>;
  totalAllocated: number;
  deficit: number;
}

// Public named-export surface (unchanged) — alias to the static member above.
export const distributeMass = EdprRequestService.distributeMass;
